// Copyright (c) Docugami, Inc. All rights reserved.

import { Check, IList, List } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { scrollIntoViewIfNeeded } from "components/doc_hosting/taskpane/TaskpaneCardHelpers";
import { backgroundGray, homeGray } from "components/shell/presentation/AppStyles";
import { useAppSelector } from "hooks/redux";
import { useEffect, useRef } from "react";
import { selectCurrentChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";
import { ChunkItemSlot, ReportChunkItemSlot } from "./ChunkItemSlot";
import { DndItem } from "./DndTypes";
import { ISlotItem, createItemSlotElementId, isChunkSlot, isReportChunkSlot } from "./ItemSlot";

const ListWrapper = styled.div`
    overflow: hidden auto;
    max-height: 250px;
`

const Root = styled.div`
    overflow: hidden;
    width: 100%;
`

export interface SelectionOptions {
    onSelectionChanged?: (ids: string[]) => void;
    selectedIds: string[];
    title?: string;
    multiselect?: boolean
}

export interface ItemSlotsProps {
    slots?: ISlotItem[];
    onDrop?: (item: ISlotItem, dndItem: DndItem) => void;
    canDrop?: (item: ISlotItem, dropItem: DndItem) => boolean;
    onClick?: (item: ISlotItem) => void;
    onRemove?: (item: ISlotItem) => void;
    collapsed?: boolean;
    hideManagementCommands?: boolean;
    selectionOptions?: SelectionOptions;
    listWrapperClassName?: string;
    scrollOnSelection?: boolean;
}

export const ItemSlots = (props: ItemSlotsProps): JSX.Element => {
    const listWrapperRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<IList | null>(null);
    const selectedInstance = useAppSelector(selectCurrentChunkInstance);

    function onRenderItem(item?: ISlotItem, index?: number, isScrolling?: boolean): JSX.Element | null {
        if (!!item) {
            if (isChunkSlot(item)) {
                const isSelected = props.selectionOptions?.selectedIds.includes(item.id);
                return (
                    <SelectionSlotWrapper
                        isSelected={isSelected}
                        disabled={!props.selectionOptions}
                        onSelect={() => {
                            if (!props.selectionOptions?.onSelectionChanged) {
                                return;
                            }
                            const selectedIds = props.selectionOptions.selectedIds;
                            if (!props.selectionOptions.multiselect && !isSelected) {
                                props.selectionOptions.onSelectionChanged([item.id]);
                            }
                            else {
                                props.selectionOptions.onSelectionChanged(isSelected ? selectedIds.filter(y => y !== item.id) : [...selectedIds, item.id])
                            }
                        }}>
                        <ChunkItemSlot
                            key={item.id}
                            item={item}
                            onDrop={!!props.onDrop ? dndItem => props.onDrop!(item, dndItem) : undefined}
                            canDrop={!!props.canDrop ? (dropItem) => props.canDrop!(item, dropItem) : undefined}
                            onClick={!!props.onClick ? () => props.onClick!(item) : undefined}
                            onRemove={!!props.onRemove ? () => props.onRemove!(item) : undefined}
                            hideManagementCommands={props.hideManagementCommands} />
                    </SelectionSlotWrapper>
                )
            }
            else if (isReportChunkSlot(item)) {
                return (
                    <ReportChunkItemSlot
                        key={item.id}
                        item={item}
                        onDrop={!!props.onDrop ? dndItem => props.onDrop?.(item, dndItem) : undefined}
                        canDrop={!!props.canDrop ? (dropItem) => props.canDrop!(item, dropItem) : undefined}
                        onClick={!!props.onClick ? () => props.onClick?.(item) : undefined}
                        hideManagementCommands={props.hideManagementCommands} />
                );
            }
        }

        return null;
    }

    // Scroll the slots list to the selected slot item
    useEffect(() => {
        if (!listWrapperRef.current || !props.slots || !props.scrollOnSelection || !selectedInstance?.spanId) {
            return;
        }

        // Note when there are more slot types that support selection, we should add that to this predicate
        const selectedSlot = props.slots.find(x => (isChunkSlot(x) && x?.spanId === selectedInstance.spanId) || (isReportChunkSlot(x) && x.binding?.spanId === selectedInstance.spanId));
        if (!selectedSlot) {
            return;
        }

        const child = listWrapperRef.current.querySelector(`[id='${createItemSlotElementId(selectedSlot.id)}']`);
        if (!!child) {
            scrollIntoViewIfNeeded(child, listWrapperRef.current, { block: "nearest" })
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedInstance?.spanId, props.scrollOnSelection])

    useEffect(() => listRef.current?.forceUpdate(), [props.selectionOptions?.selectedIds]);

    const allSelected = !!props.selectionOptions && !!props.slots && props.selectionOptions.selectedIds.length === props.slots?.length;
    return (
        <Root>
            {!!props.selectionOptions &&
                <SelectionHeader
                    isAllSelected={allSelected}
                    onToggle={() => allSelected ? props.selectionOptions?.onSelectionChanged?.([]) : props.selectionOptions?.onSelectionChanged?.((props.slots ?? []).map(x => x.id))}
                    title={props.selectionOptions?.title}
                    multiselect={props.selectionOptions?.multiselect} />
            }
            <ListWrapper ref={listWrapperRef} data-is-scrollable={true} className={props.listWrapperClassName}>
                <List
                    componentRef={listRef}
                    items={props.slots}
                    onRenderCell={onRenderItem}
                    onShouldVirtualize={() => !!props.slots && props.slots.length > 10}
                    renderedWindowsAhead={0}
                    renderedWindowsBehind={0}
                />
            </ListWrapper>
        </Root>
    );
}

const SelectionRoot = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 10px;
    align-items: center;
`

const Content = styled.div`
    width: 100%;
`

const CheckContainer = styled.div`
    height: 100%;
`

interface SelectionSlotWrapperProps {
    isSelected?: boolean;
    onSelect?: () => void;
    children?: JSX.Element | JSX.Element[] | null;
    disabled?: boolean;
}

const SelectionSlotWrapper = (props: SelectionSlotWrapperProps): JSX.Element => {
    return props.disabled ? (
        <>
            {props.children}
        </>
    ) : (
        <SelectionRoot>
            <CheckContainer onClick={props.onSelect}>
                <Check checked={props.isSelected} />
            </CheckContainer>
            <Content>
                {props.children}
            </Content>
        </SelectionRoot>
    )
}

const SelectionHeaderRoot = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    gap: 10px;
    border-bottom: 2px solid ${backgroundGray};
    box-sizing: border-box;
    padding-bottom: 5px;
    margin-bottom: 10px;
`

const SelectionHeaderTitle = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: ${dgFontFamily};
    color: ${homeGray};
    font-weight: 700;
    font-size: 11px;
`

interface SelectionHeaderProps {
    title?: string;
    onToggle?: () => void;
    isAllSelected?: boolean;
    multiselect?: boolean
}

const SelectionHeader = (props: SelectionHeaderProps): JSX.Element => {
    return (
        <SelectionHeaderRoot>
            {props.multiselect &&
                <div onClick={props.onToggle}>
                    <Check checked={props.isAllSelected} />
                </div>
            }
            <SelectionHeaderTitle>
                {props.title}
            </SelectionHeaderTitle>
        </SelectionHeaderRoot>
    )
}
